/**
 * Shared-element transitions.
 *
 * The router already wraps navigation in `startViewTransition`, but nothing in the app ever
 * set a `view-transition-name`, so every navigation was a whole-page cross-fade and the
 * brief's "shared-element transitions into the sample editor" had no implementation at all.
 *
 * A shared element is two DOM nodes on two screens that the browser is told are the same
 * thing. The rule the API enforces is that a name must be unique *at the moment the snapshot
 * is taken*, which is why the name is claimed just before navigating and released once the
 * transition is over — a stale name on a hidden screen silently disables the whole effect.
 */

const SURFACE = 'padlab-editor-surface';

let claimed: HTMLElement | null = null;

function supportsViewTransitions(): boolean {
  return typeof document !== 'undefined' && typeof (document as { startViewTransition?: unknown }).startViewTransition === 'function';
}

/**
 * Names `element` as the thing the sample editor's waveform grows out of.
 *
 * Call it immediately before navigating to the editor. Safe to call when the browser has no
 * View Transitions — it does nothing rather than leaving an inert style behind.
 */
export function claimEditorSurface(element: HTMLElement | null | undefined): void {
  if (!supportsViewTransitions() || element === null || element === undefined) return;
  releaseEditorSurface();
  element.style.viewTransitionName = SURFACE;
  claimed = element;
}

/** Gives the name back. The editor calls this once its own surface has taken it over. */
export function releaseEditorSurface(): void {
  if (claimed === null) return;
  claimed.style.removeProperty('view-transition-name');
  claimed = null;
}

/**
 * Adopts the name on the editor's own surface, and returns a disposer.
 *
 * Deliberately not permanent: leaving the name on a screen the user has navigated away from
 * would make it the shared element of the *next* transition too, which produces a waveform
 * flying out of an unrelated screen.
 */
export function adoptEditorSurface(element: HTMLElement): () => void {
  if (!supportsViewTransitions()) return (): void => {};
  releaseEditorSurface();
  element.style.viewTransitionName = SURFACE;
  return (): void => {
    element.style.removeProperty('view-transition-name');
  };
}
